import type { IntlShape } from 'react-intl';

import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics/api';
import { pasteOptionsToolbarMessages as messages } from '@atlaskit/editor-common/messages/paste-options-toolbar';
import type { Command, CommandDispatch } from '@atlaskit/editor-common/types/command';
import type {
	FloatingToolbarConfig,
	FloatingToolbarDropdown,
	FloatingToolbarItem,
} from '@atlaskit/editor-common/types/floating-toolbar';
import type { LastContentPasted } from '@atlaskit/editor-plugin-paste/paste-plugin-type';
import type { ResolvedPos } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { akEditorFloatingPanelZIndex } from '@atlaskit/editor-shared-styles/constants';
import ClipboardIcon from '@atlaskit/icon/core/clipboard';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import {
	changeToMarkdownWithAnalytics,
	changeToPlainTextWithAnalytics,
	changeToRichTextWithAnalytics,
	dropdownClickHandler,
} from '../editor-commands/commands';
import type { MarkdownToPmConverter } from '../pasteOptionsToolbarPluginType';
import {
	PASTE_OPTIONS_TEST_ID,
	PASTE_TOOLBAR_CLASS,
	PASTE_TOOLBAR_ITEM_CLASS,
} from '../pm-plugins/constants';
import {
	hasLinkMark,
	hasMediaNode,
	hasRuleNode,
	isPastedFromFabricEditor,
} from '../pm-plugins/util';
import type { PasteOptionsPluginState, Position } from '../types/types';
import { pasteOptionsPluginKey, ToolbarDropdownOption } from '../types/types';

const TOP_LEVEL_LIST_NODE_NAMES = ['bulletList', 'orderedList', 'taskList'];

/**
 * Pasting markdown lists leaves the cursor at doc > list > listItem > paragraph, so the
 * immediate grandparent is a listItem rather than the doc and the paste options are never
 * offered. Treat a list that is itself a direct child of the doc as top level, while still
 * excluding lists nested in other containers such as tables and panels.
 */
const isInsideTopLevelList = ($from: ResolvedPos): boolean => {
	if (!isExperimentEnabled('platform_editor_markdown_conversion_improvements')) {
		return false;
	}

	if ($from.depth < 1) {
		return false;
	}

	return TOP_LEVEL_LIST_NODE_NAMES.includes($from.node(1).type.name);
};

export const isToolbarVisible = (
	state: EditorState,
	lastContentPasted: LastContentPasted,
): boolean => {
	/**
	 * Conditions for not showing the toolbar:
	 * 1. Pasting link, media or text containing media(note: markdown link and images are allowed)
	 * 2. Content is pasted in a nested node(i.e. inside a table, panel etc.).
	 *    (grandParent node should be root doc for showing up the toolbar)
	 * 3. Cursor is inside the codeblock.
	 */
	const $from = state.selection.$from;
	if (hasRuleNode(lastContentPasted.pastedSlice, state.schema)) {
		return false;
	}
	const parentNodeType = $from.parent?.type;
	const grandParentNodeType = $from.node($from.depth - 1)?.type;

	if (
		(grandParentNodeType?.name === state.schema.nodes.doc.name || isInsideTopLevelList($from)) &&
		parentNodeType.name !== state.schema.nodes.codeBlock?.name &&
		!isPastedFromFabricEditor(lastContentPasted.pasteSource) &&
		!hasLinkMark(lastContentPasted.pastedSlice) &&
		!hasMediaNode(lastContentPasted.pastedSlice)
	) {
		return true;
	}

	return false;
};

export const getToolbarMenuConfig = (
	pluginState: PasteOptionsPluginState,
	intl: IntlShape,
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	markdownToPmConverter?: MarkdownToPmConverter,
): FloatingToolbarDropdown<Command> => {
	const options = [
		{
			id: 'editor.paste.richText',
			title: intl.formatMessage(messages.richText),
			selected: pluginState.selectedOption === ToolbarDropdownOption.RichText,
			hidden: pluginState.isPlainText,
			onClick: changeToRichTextWithAnalytics(editorAnalyticsAPI)(),
		},
		{
			id: 'editor.paste.markdown',
			title: intl.formatMessage(messages.markdown),
			selected: pluginState.selectedOption === ToolbarDropdownOption.Markdown,
			onClick: changeToMarkdownWithAnalytics(
				editorAnalyticsAPI,
				pluginState.plaintext.length,
				undefined,
				markdownToPmConverter,
			)(),
		},
		{
			id: 'editor.paste.plainText',
			title: intl.formatMessage(messages.plainText),
			selected: pluginState.selectedOption === ToolbarDropdownOption.PlainText,
			onClick: changeToPlainTextWithAnalytics(editorAnalyticsAPI, pluginState.plaintext.length)(),
		},
	];

	return {
		id: PASTE_TOOLBAR_ITEM_CLASS,
		icon: ClipboardIcon,
		type: 'dropdown',
		testId: PASTE_OPTIONS_TEST_ID,
		title: intl.formatMessage(messages.pasteOptions),
		options,
		onToggle: onToggleHandler,
	};
};

const onToggleHandler = (state: EditorState, dispatch: CommandDispatch | undefined) => {
	return dropdownClickHandler()(state, dispatch);
};

export const buildToolbar = (
	state: EditorState,
	intl: IntlShape,
	editorAnalyticsAPI: EditorAnalyticsAPI | undefined,
	markdownToPmConverter?: MarkdownToPmConverter,
): FloatingToolbarConfig | undefined => {
	const { schema } = state;
	const validNodes = Object.values(schema.nodes);

	const pluginState = pasteOptionsPluginKey.getState(state);
	const menu: FloatingToolbarItem<Command> = getToolbarMenuConfig(
		pluginState,
		intl,
		editorAnalyticsAPI,
		markdownToPmConverter,
	);

	return {
		title: intl.formatMessage(messages.pasteOptions),
		nodeType: validNodes,
		zIndex: akEditorFloatingPanelZIndex,
		className: PASTE_TOOLBAR_CLASS,
		items: [menu],
		align: 'right',
		onPositionCalculated,
	};
};

const onPositionCalculated = (editorView: EditorView, _nextPos: Position) => {
	const { from } = editorView.state.selection;
	const fromCoords = editorView.coordsAtPos(from);

	// Ignored via go/ees005
	// eslint-disable-next-line @atlaskit/editor/no-as-casting
	const toolbar = document.querySelector(
		`div[aria-label="${messages.pasteOptions.defaultMessage}"]`,
	) as HTMLElement;

	const offsetParent = toolbar?.offsetParent || editorView.dom;
	const offsetParentRect = offsetParent?.getBoundingClientRect();

	const offsetTop = offsetParentRect?.top || 0;
	const offsetLeft = offsetParentRect?.left || 0;
	const offsetScrollTop = offsetParent?.scrollTop || 0;

	const cursorHeight = getCursorHeight(editorView, from);

	return {
		top: fromCoords.top - offsetTop + offsetScrollTop + cursorHeight,
		left: fromCoords.left - offsetLeft,
	};
};

const getCursorHeight = (editorView: EditorView, from: number): number => {
	const nodeAtFrom = editorView.domAtPos(from).node;
	const nearestNonTextNode =
		nodeAtFrom?.nodeType === Node.TEXT_NODE
			? // Ignored via go/ees005
				// eslint-disable-next-line @atlaskit/editor/no-as-casting
				(nodeAtFrom.parentNode as HTMLElement)
			: // Ignored via go/ees005
				// eslint-disable-next-line @atlaskit/editor/no-as-casting
				(nodeAtFrom as HTMLElement);

	return parseFloat(window.getComputedStyle(nearestNonTextNode, undefined).lineHeight || '');
};
